import { useRef, type JSX, type KeyboardEvent } from "react";
import type { ServerBrowserCity, ServerBrowserEntry } from "@stahlfront/shared";

const LANGUAGE_LABEL: Record<string, string> = {
  "eesti keel": "eesti keel",
  en: "English",
  ru: "русский",
};

const LOAD_STATE_LABEL: Record<string, string> = {
  NORMAL: "Обычн.",
  BUSY: "Битком",
  FULL: "Заполнено",
};

const LOAD_STATE_TONE: Record<string, "normal" | "busy" | "full"> = {
  NORMAL: "normal",
  BUSY: "busy",
  FULL: "full",
};

function ResourcePill({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="sb-res" aria-label={`${label} stockpile placeholder`}>
      <span className="sb-res-label">{label}</span>
      <strong className="sb-res-value">{value}</strong>
    </span>
  );
}

export function SbTopBar(): JSX.Element {
  return (
    <header className="sb-topbar" aria-label="Server selection">
      <div className="sb-topbar-left">
        <div className="sb-logo" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="22" height="22">
            <path
              d="M6 4 L26 4 L22 16 L26 28 L6 28 L10 16 Z"
              fill="oklch(70% 0.16 60)"
              stroke="oklch(36% 0.04 55)"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="sb-title">Выбрать город</h2>
      </div>
      <div className="sb-topbar-right" aria-label="Account stockpile (preview)">
        <ResourcePill label="PROV" value="--" />
        <ResourcePill label="TRSY" value="--" />
        <ResourcePill label="RESV" value="--" />
        <ResourcePill label="DIA" value="--" />
      </div>
    </header>
  );
}

export function SbLegend(): JSX.Element {
  return (
    <ul className="sb-legend" aria-label="City availability legend">
      <li>
        <span className="sb-diamond sb-diamond-full" aria-hidden="true" />
        <span>Заполнено</span>
      </li>
      <li>
        <span className="sb-diamond sb-diamond-busy" aria-hidden="true" />
        <span>Битком</span>
      </li>
      <li>
        <span className="sb-diamond sb-diamond-normal" aria-hidden="true" />
        <span>Обычн.</span>
      </li>
    </ul>
  );
}

export interface SbServerSectionProps {
  readonly entry: ServerBrowserEntry;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onPickCity: (city: ServerBrowserCity) => void;
}

export function SbServerSection({
  entry,
  open,
  onToggle,
  onPickCity,
}: SbServerSectionProps): JSX.Element {
  const headerId = `sb-server-${entry.server.id}-header`;
  const panelId = `sb-server-${entry.server.id}-panel`;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const cellCount = entry.cities.length;

  function focusTile(index: number): void {
    const grid = gridRef.current;
    if (!grid) return;
    const tiles = grid.querySelectorAll<HTMLButtonElement>(".sb-city-tile");
    const target = tiles[Math.max(0, Math.min(tiles.length - 1, index))];
    target?.focus();
  }

  function onKey(idx: number, ev: KeyboardEvent<HTMLButtonElement>): void {
    const cols = 4;
    if (ev.key === "ArrowRight") {
      ev.preventDefault();
      focusTile(idx + 1);
    } else if (ev.key === "ArrowLeft") {
      ev.preventDefault();
      focusTile(idx - 1);
    } else if (ev.key === "ArrowDown") {
      ev.preventDefault();
      focusTile(Math.min(cellCount - 1, idx + cols));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      focusTile(Math.max(0, idx - cols));
    } else if (ev.key === "Home") {
      ev.preventDefault();
      focusTile(0);
    } else if (ev.key === "End") {
      ev.preventDefault();
      focusTile(cellCount - 1);
    }
  }

  return (
    <section className="sb-server-section">
      <button
        id={headerId}
        type="button"
        className="sb-server-header"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="sb-server-name">
          <span className="sb-server-glyph" aria-hidden="true">
            ◐
          </span>
          Сервер {entry.server.id}
        </span>
        <span className="sb-server-meta">
          {entry.server.currentPlayers}/{entry.server.maxPlayers}
        </span>
        <span className={`sb-chevron ${open ? "sb-chevron-up" : ""}`} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14">
            <path
              d="M6 9 L12 15 L18 9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        className={`sb-city-panel ${open ? "sb-city-panel-open" : ""}`}
      >
        <div className="sb-city-grid" ref={gridRef} role="grid" aria-label={`Cities on Сервер ${entry.server.id}`}>
          {entry.cities.map((c, i) => (
            <SbCityTile
              key={`${c.serverId}-${c.id}`}
              city={c}
              language={entry.server.language}
              tileIndex={i}
              onPick={() => onPickCity(c)}
              onKey={(ev) => onKey(i, ev)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface SbCityTileProps {
  readonly city: ServerBrowserCity;
  readonly language: string;
  readonly tileIndex: number;
  readonly onPick: () => void;
  readonly onKey: (ev: KeyboardEvent<HTMLButtonElement>) => void;
}

function SbCityTile({ city, language, tileIndex, onPick, onKey }: SbCityTileProps): JSX.Element {
  const tone = LOAD_STATE_TONE[city.loadState] ?? "normal";
  const langLabel = LANGUAGE_LABEL[language] ?? language;
  // Display id is the city's seed-stable position within the server's roster (1-based),
  // mirroring Vadim's reference where tiles read `Сервер 200 #10001 … #10016`.
  const displayId = 10000 + tileIndex + 1;
  return (
    <div role="gridcell">
      <button
        type="button"
        className="sb-city-tile"
        data-tone={tone}
        onClick={onPick}
        onKeyDown={onKey}
        aria-label={`${city.displayName}, Сервер ${city.serverId} #${displayId}, ${LOAD_STATE_LABEL[city.loadState] ?? city.loadState}`}
      >
        <span className="sb-lang" aria-hidden="true">
          {langLabel}
        </span>
        <span className="sb-city-caption">
          <span className={`sb-diamond sb-diamond-${tone}`} aria-hidden="true" />
          <span className="sb-city-id">
            Сервер {city.serverId} #{displayId}
          </span>
        </span>
        <span className="sb-city-name">{city.displayName}</span>
      </button>
    </div>
  );
}
